import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { Plus, Pencil, Trash2, Search, FileText, Upload, X as XIcon, Mail, Gauge } from "lucide-react";
import { toast } from "sonner";

const EMPTY_FORM = {
  description: "", id_no: "", serial_no: "", range: "", least_count: "", make: "",
  calibration_frequency_years: "1", calibration_type: "EXT", location: "",
  cal_date: "", due_date: "", notes: "",
};

const STATUS_BADGE = {
  overdue: "bg-red-50 text-red-700",
  due_soon: "bg-amber-50 text-amber-700",
  ok: "bg-emerald-50 text-emerald-700",
  unscheduled: "bg-slate-100 text-slate-600",
};
const STATUS_LABEL = {
  overdue: "Overdue",
  due_soon: "Due Soon",
  ok: "OK",
  unscheduled: "Unscheduled",
};

// Report cell — mirrors the Items page's ItemImageCell: fetches the file as a
// blob (so the Bearer token attaches) rather than linking straight to the API
// URL, and lets Quality upload/replace/view/remove the one report attached
// to this instrument.
function ReportCell({ instrument, onChanged }) {
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const hasReport = !!instrument.has_report;

  const view = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/calibration/instruments/${instrument.id}/report`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      toast.error("Could not open report");
    } finally {
      setBusy(false);
    }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/calibration/instruments/${instrument.id}/report`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Calibration report uploaded");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove the attached calibration report?")) return;
    setBusy(true);
    try {
      await api.delete(`/calibration/instruments/${instrument.id}/report`);
      toast.success("Report removed");
      onChanged?.();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-2" data-testid={`cal-report-cell-${instrument.id_no || instrument.sl_no}`}>
      {hasReport ? (
        <button
          type="button"
          onClick={view}
          disabled={busy}
          className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 disabled:opacity-40"
          data-testid={`cal-report-view-${instrument.sl_no}`}
        >
          <FileText className="w-3.5 h-3.5" /> {instrument.report_filename || "View"}
        </button>
      ) : (
        <span className="text-xs text-slate-400">No report</span>
      )}
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={busy}
        className="text-[10px] uppercase tracking-wider text-slate-500 hover:text-blue-600 disabled:opacity-40"
        data-testid={`cal-report-upload-${instrument.sl_no}`}
        title={hasReport ? "Replace report" : "Attach calibration report"}
      >
        <Upload className="w-3.5 h-3.5" />
      </button>
      {hasReport && (
        <button
          type="button"
          onClick={remove}
          disabled={busy}
          className="text-[10px] uppercase tracking-wider text-slate-500 hover:text-red-600 disabled:opacity-40"
          data-testid={`cal-report-remove-${instrument.sl_no}`}
          title="Remove report"
        >
          <XIcon className="w-3.5 h-3.5" />
        </button>
      )}
      <input
        ref={fileRef} type="file" accept="application/pdf,image/png,image/jpeg,image/webp"
        className="hidden" onChange={upload}
        data-testid={`cal-report-input-${instrument.sl_no}`}
      />
    </div>
  );
}

export default function CalibrationPage() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // instrument being edited, or null for "new"
  const [form, setForm] = useState(EMPTY_FORM);
  const [busySave, setBusySave] = useState(false);
  const [busyReminder, setBusyReminder] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/calibration/instruments");
      setList(data);
    } catch {
      toast.error("Failed to load instruments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((i) => [i.description, i.id_no, i.serial_no, i.make, i.location]
      .some((v) => (v || "").toLowerCase().includes(q)));
  }, [list, search]);

  const counts = useMemo(() => {
    const c = { total: list.length, overdue: 0, due_soon: 0, ok: 0, unscheduled: 0 };
    for (const i of list) c[i.due_status] = (c[i.due_status] || 0) + 1;
    return c;
  }, [list]);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setOpen(true);
  };

  const openEdit = (inst) => {
    setEditing(inst);
    setForm({
      description: inst.description || "", id_no: inst.id_no || "", serial_no: inst.serial_no || "",
      range: inst.range || "", least_count: inst.least_count || "", make: inst.make || "",
      calibration_frequency_years: inst.calibration_frequency_years ?? "",
      calibration_type: inst.calibration_type || "EXT", location: inst.location || "",
      cal_date: inst.cal_date || "", due_date: inst.due_date || "", notes: inst.notes || "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.description.trim()) return toast.error("Instrument/Equipment description is required");
    setBusySave(true);
    const payload = {
      ...form,
      calibration_frequency_years: form.calibration_frequency_years === "" ? null : parseFloat(form.calibration_frequency_years),
    };
    try {
      if (editing) {
        await api.patch(`/calibration/instruments/${editing.id}`, payload);
        toast.success("Instrument updated");
      } else {
        await api.post("/calibration/instruments", payload);
        toast.success("Instrument added");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setBusySave(false);
    }
  };

  const remove = async (inst) => {
    if (!window.confirm(`Remove "${inst.description}" (${inst.id_no || "no ID"})?`)) return;
    try {
      await api.delete(`/calibration/instruments/${inst.id}`);
      toast.success("Instrument removed");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
    }
  };

  const sendReminderNow = async () => {
    setBusyReminder(true);
    try {
      const { data } = await api.post("/calibration/due-check/run");
      if (data.status === "no_due_instruments") toast.success("No instruments are due within the reminder window right now.");
      else if (data.status === "sent") toast.success(`Reminder sent for ${data.instrument_count} instrument(s).`);
      else if (data.status === "queued") toast.warning("Reminder queued — no RESEND_API_KEY configured on the server yet.");
      else toast.info(`Due-check ran: ${data.status}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not run the due-check");
    } finally {
      setBusyReminder(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="calibration-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Quality</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>
            Calibration
          </h1>
          <div className="text-xs text-slate-500 mt-1">Master List of Instrument &amp; Gauges — CAL-7.1.5.2-F2-001</div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={sendReminderNow} disabled={busyReminder} data-testid="cal-send-reminder-btn">
            <Mail className="w-4 h-4 mr-1.5" /> {busyReminder ? "Sending..." : "Send Reminder Now"}
          </Button>
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={openNew} data-testid="cal-new-instrument-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Add Instrument
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Total Instruments</div>
          <div className="text-2xl font-extrabold text-slate-900 mt-1">{counts.total}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Overdue</div>
          <div className="text-2xl font-extrabold text-red-600 mt-1">{counts.overdue || 0}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Due within 15 days</div>
          <div className="text-2xl font-extrabold text-amber-600 mt-1">{counts.due_soon || 0}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Up to Date</div>
          <div className="text-2xl font-extrabold text-emerald-600 mt-1">{counts.ok || 0}</div>
        </div>
      </div>

      <div className="relative max-w-sm">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input
          className="pl-9"
          placeholder="Search instrument, ID, make, location..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="cal-search-input"
        />
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm whitespace-nowrap" data-testid="cal-instruments-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-slate-500 border-b border-slate-100">
                <th className="py-2.5 px-3">Sl.No</th>
                <th className="py-2.5 px-3">Instrument / Equipment</th>
                <th className="py-2.5 px-3">ID No</th>
                <th className="py-2.5 px-3">Serial No</th>
                <th className="py-2.5 px-3">Range</th>
                <th className="py-2.5 px-3">L.C.</th>
                <th className="py-2.5 px-3">Make</th>
                <th className="py-2.5 px-3">Freq (Yrs)</th>
                <th className="py-2.5 px-3">INT/EXT</th>
                <th className="py-2.5 px-3">Location</th>
                <th className="py-2.5 px-3">Cal. Date</th>
                <th className="py-2.5 px-3">Due Date</th>
                <th className="py-2.5 px-3">Status</th>
                <th className="py-2.5 px-3">Report</th>
                <th className="py-2.5 px-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((inst) => (
                <tr key={inst.id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`cal-row-${inst.sl_no}`}>
                  <td className="py-2 px-3 mono text-slate-500">{inst.sl_no}</td>
                  <td className="py-2 px-3 font-medium text-slate-800">{inst.description}</td>
                  <td className="py-2 px-3 mono text-slate-600">{inst.id_no}</td>
                  <td className="py-2 px-3 mono text-slate-600">{inst.serial_no}</td>
                  <td className="py-2 px-3 text-slate-600">{inst.range}</td>
                  <td className="py-2 px-3 text-slate-600">{inst.least_count}</td>
                  <td className="py-2 px-3 text-slate-600">{inst.make}</td>
                  <td className="py-2 px-3 text-slate-600">{inst.calibration_frequency_years ?? "—"}</td>
                  <td className="py-2 px-3 text-slate-600">{inst.calibration_type}</td>
                  <td className="py-2 px-3 text-slate-600">{inst.location}</td>
                  <td className="py-2 px-3 mono text-slate-600">{inst.cal_date || "—"}</td>
                  <td className="py-2 px-3 mono text-slate-600">{inst.due_date || "—"}</td>
                  <td className="py-2 px-3">
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS_BADGE[inst.due_status]}`}>
                      {STATUS_LABEL[inst.due_status]}
                      {inst.due_status !== "unscheduled" && inst.due_status !== "overdue" ? ` · ${inst.days_remaining}d` : ""}
                      {inst.due_status === "overdue" ? ` · ${Math.abs(inst.days_remaining)}d` : ""}
                    </span>
                  </td>
                  <td className="py-2 px-3"><ReportCell instrument={inst} onChanged={load} /></td>
                  <td className="py-2 px-3">
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(inst)} data-testid={`cal-edit-${inst.sl_no}`}>
                        <Pencil className="w-4 h-4 text-slate-500" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => remove(inst)} data-testid={`cal-delete-${inst.sl_no}`}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={15} className="px-4 py-10 text-center text-sm text-slate-500">
                  {list.length === 0 ? "No instruments yet — add one to get started." : "No instruments match your search."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Gauge className="w-4 h-4 text-blue-600" /> {editing ? "Edit Instrument" : "Add Instrument"}
            </SheetTitle>
          </SheetHeader>
          <div className="space-y-4 mt-6">
            <div>
              <Label>Instrument / Equipment Description</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="cal-form-description" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>ID No</Label><Input value={form.id_no} onChange={(e) => setForm({ ...form, id_no: e.target.value })} /></div>
              <div><Label>Serial No</Label><Input value={form.serial_no} onChange={(e) => setForm({ ...form, serial_no: e.target.value })} /></div>
              <div><Label>Range</Label><Input value={form.range} onChange={(e) => setForm({ ...form, range: e.target.value })} /></div>
              <div><Label>L.C. (Least Count)</Label><Input value={form.least_count} onChange={(e) => setForm({ ...form, least_count: e.target.value })} /></div>
              <div><Label>Make</Label><Input value={form.make} onChange={(e) => setForm({ ...form, make: e.target.value })} /></div>
              <div><Label>Calibration Frequency (Years)</Label>
                <Input type="number" step="0.5" value={form.calibration_frequency_years}
                  onChange={(e) => setForm({ ...form, calibration_frequency_years: e.target.value })} />
              </div>
              <div>
                <Label>Calibration INT/EXT</Label>
                <Select value={form.calibration_type} onValueChange={(v) => setForm({ ...form, calibration_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="INT">Internal</SelectItem>
                    <SelectItem value="EXT">External</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
              <div><Label>Cal. Date</Label><Input type="date" value={form.cal_date} onChange={(e) => setForm({ ...form, cal_date: e.target.value })} data-testid="cal-form-cal-date" /></div>
              <div><Label>Due Date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="cal-form-due-date" /></div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <p className="text-xs text-slate-500">
              After the instrument comes back from the supplier, save its new Cal./Due Date here and attach the
              calibration certificate from the Report column in the table — that's what clears it from the daily
              reminder.
            </p>
          </div>
          <SheetFooter className="mt-6">
            <Button onClick={save} disabled={busySave} className="bg-blue-600 hover:bg-blue-700" data-testid="cal-form-save">
              {busySave ? "Saving..." : editing ? "Save Changes" : "Add Instrument"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}
